import { supabase } from "../supabase/client";

const clean = (value) => {
  const text = `${value ?? ""}`.trim();
  return text || null;
};

const buildProfilePayload = (client, company, companyName) => ({
  role: "first",
  email: clean(client.email),
  driver_name: clean(client.name),
  phone: clean(client.phone),
  position: clean(client.position),
  branch: clean(client.branch),
  user_id: clean(client.user_id),
  company,
  company_name: clean(client.company_name) || clean(companyName) || company,
  memo: clean(client.note),
  hire_date: clean(client.hire_date),
});

const findProfileForClient = async ({ email, company, user_id, fallbackEmail, fallbackUserId }) => {
  const emails = [clean(email), clean(fallbackEmail)].filter(Boolean);
  for (const targetEmail of emails) {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, role")
      .eq("company", company)
      .eq("email", targetEmail)
      .limit(1);
    if (error) throw error;
    if (data?.[0]?.id) {
      if (data[0].role && !["first", "operator"].includes(data[0].role)) {
        throw new Error(`${targetEmail} 이메일은 이미 ${data[0].role} 권한의 profiles 계정으로 등록되어 있습니다.`);
      }
      return data[0];
    }
  }

  const userIds = [clean(user_id), clean(fallbackUserId)].filter(Boolean);
  for (const targetUserId of userIds) {
    const { data, error } = await supabase
      .from("profiles")
      .select("id")
      .eq("company", company)
      .in("role", ["first", "operator"])
      .eq("user_id", targetUserId)
      .limit(1);
    if (error) throw error;
    if (data?.[0]?.id) return data[0];
  }

  return null;
};

export const syncClientToFirstProfile = async (client, options = {}) => {
  const company = clean(options.company || client.company);
  if (!company) throw new Error("회사 정보가 없어 profiles 동기화를 할 수 없습니다.");

  const payload = buildProfilePayload(client, company, options.companyName || client.company_name);
  const existing = await findProfileForClient({
    email: payload.email,
    company,
    user_id: payload.user_id,
    fallbackEmail: options.previousEmail,
    fallbackUserId: options.previousUserId,
  });

  if (existing?.id) {
    const { error } = await supabase
      .from("profiles")
      .update(payload)
      .eq("id", existing.id)
      .eq("company", company);
    if (error) throw error;
    return existing.id;
  }

  const { data, error } = await supabase.from("profiles").insert(payload).select("id").single();
  if (error) throw error;
  return data?.id || null;
};

export const deleteFirstProfileForClient = async (client, options = {}) => {
  const company = clean(options.company || client.company);
  if (!company) return;

  const existing = await findProfileForClient({
    email: client.email,
    company,
    user_id: client.user_id,
  });
  if (!existing?.id) return;

  const { error } = await supabase
    .from("profiles")
    .delete()
    .eq("id", existing.id)
    .eq("company", company)
    .eq("role", "first");
  if (error) throw error;
};
